"use client"

import type { LucideIcon } from "lucide-react"

import { accountText, type LocaleProps } from "../../personal-web"

type Props = LocaleProps & {
  /** Anchor id so the divider above can smooth-scroll to this header. */
  id: string
  /** Step number badge — keeps Before / Install / Connect visually numbered. */
  step: 1 | 2 | 3
  /** i18n key for the section title. */
  titleKey: string
  /** Optional i18n key for the subtitle. */
  subtitleKey?: string
  icon: LucideIcon
}

/**
 * Big numbered section header. Used twice on the page — once for "Install
 * your eSIM" and once for "Connect to the network" — to break the long
 * step list into chewable phases.
 */
export function SectionHeader({
  lng,
  id,
  step,
  titleKey,
  subtitleKey,
  icon: Icon,
}: Props) {
  return (
    <header
      id={id}
      className="scroll-mt-20 flex items-start gap-3 px-1 pt-2 md:gap-4"
    >
      <span
        aria-hidden
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-[0_8px_20px_-10px_rgba(234,88,12,0.6)]"
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-orange-600">
            {accountText(lng, "install.section.step", { n: step })}
          </span>
        </div>
        <h2 className="mt-0.5 text-[22px] font-extrabold leading-tight tracking-tight text-slate-950 md:text-[26px]">
          {accountText(lng, titleKey)}
        </h2>
        {subtitleKey ? (
          <p className="mt-1 text-sm text-stone-500">
            {accountText(lng, subtitleKey)}
          </p>
        ) : null}
      </div>
    </header>
  )
}
